import React, { useContext, useEffect, useState } from 'react';
import { Alert, Modal, ProgressBar } from 'react-bootstrap';
import { ExclamationTriangle } from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import { Link, Redirect, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LanguagePicker from '../components/common/LanguagePicker';
import CircularProgressBar from '../components/common/circular-progress';
import { UserContext } from '../context/UserContext';
import { makeApiRequests } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { validateEmail } from '../helpers/global';
import { BrandAside, LabeledInputField, authSubmitButtonClass, authLinkClass } from '../components/common/authUi';

const formStackClass = 'flex flex-col gap-2.5 sm:gap-3 md:gap-5 lg:gap-6';

const emptyForm = { password: '', confirmPassword: '' };
const emptyResendForm = { email: '' };

const PASSWORD_MISMATCH = 'Passwords do not match';

function CreatePasswordLoadingState() {
  return (
    <div
      className="flex min-h-[40vh] w-full grow items-center justify-center self-center py-5"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <CircularProgressBar size={5} />
    </div>
  );
}

function CreatePasswordForm({
  user,
  values,
  errors,
  submitting,
  apiError,
  onSubmit,
  onFieldChange
}) {
  const { translate } = useContext(LocalizeContext);

  return (
    <>
      <p className="mb-2 text-sm text-gray-500 sm:mb-3">
        {translate('hello')} <strong>{user?.name}</strong>, {translate('welcome_message')}
      </p>
      <div className="mb-8 sm:mb-10">
        <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
          {translate('create_password_title')}
        </h1>
        <p className="mt-2 text-sm text-gray-500">{translate('create_password_subtitle')}</p>
      </div>

      <form className={formStackClass} onSubmit={onSubmit} noValidate>
        <LabeledInputField
          id="reset-pw-password"
          label={translate('password')}
          type="password"
          autoComplete="new-password"
          value={values.password}
          error={errors.password}
          onValueChange={v => onFieldChange('password', v)}
          disabled={submitting}
        />
        <LabeledInputField
          id="reset-pw-confirm"
          label={translate('confirm_password')}
          type="password"
          autoComplete="new-password"
          value={values.confirmPassword}
          error={errors.confirmPassword}
          onValueChange={v => onFieldChange('confirmPassword', v)}
          disabled={submitting}
        />
        <button type="submit" className={authSubmitButtonClass} disabled={submitting}>
          {translate('submit')}
        </button>
      </form>

      {submitting && (
        <ProgressBar
          className="mt-5"
          striped
          animated
          variant="dark"
          now={100}
          label={`${translate('please_wait')}...`}
        />
      )}
      {apiError && (
        <Alert className="mt-4 text-sm" variant="danger">
          {apiError}
        </Alert>
      )}

      <div className="mt-5 border-t border-brand-navy/8 pt-4 text-center sm:mt-6 sm:pt-5">
        <Link to="/login" className={authLinkClass}>
          {translate('go_to_login_page')}
        </Link>
      </div>
    </>
  );
}

/** Shown when the key is missing, expired, or not recognized (API error). */
function InvalidResetTokenSection({ onResendClick }) {
  const { translate } = useContext(LocalizeContext);

  return (
    <>
      <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight sm:mb-1 sm:text-4xl">
        {translate('reset_password')}
      </h1>

      <div className="flex flex-col gap-3 sm:gap-3.5">
        <p className="m-0 max-w-prose text-sm leading-relaxed text-gray-500">
          {translate('token_expired_info')}
        </p>

        <div role="alert" className="mb-3">
          <div className="flex items-start gap-2.5 sm:items-center sm:gap-3">
            <span className="mt-0.5 inline-flex shrink-0 text-red-600 sm:mt-0 sm:translate-y-px" aria-hidden>
              <ExclamationTriangle className="block h-[1.125rem] w-[1.125rem] sm:h-5 sm:w-5" />
            </span>
            <h2 className="mb-0 min-w-0 font-sans text-sm font-bold leading-snug text-red-800 sm:text-base">
              {translate('token_expired_invalid')}
            </h2>
          </div>
        </div>

        <button type="button" className={authSubmitButtonClass} onClick={onResendClick}>
          {translate('resend_password_link')}
        </button>
      </div>

      <div className="mt-3 border-t border-brand-navy/8 pt-3 text-center">
        <Link to="/login" className={authLinkClass}>
          {translate('go_to_login_page')}
        </Link>
      </div>
    </>
  );
}

function ResendPasswordModal({ open, onClose, submitting, email, emailError, onEmailChange, apiError, onSubmitForm }) {
  const { translate } = useContext(LocalizeContext);

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      backdrop="static"
      contentClassName="overflow-hidden rounded-2xl border-0 shadow-xl"
    >
      <Modal.Header
        closeButton={!submitting}
        className="border-b border-brand-navy/8 bg-brand-surface px-3 py-2.5 sm:px-4 sm:py-3"
      >
        <Modal.Title as="div" className="font-serif text-base font-semibold text-brand-navy sm:text-lg">
          {translate('resend_password_link')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white px-3 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
        {!submitting ? (
          <>
            <p className="mb-3 text-xs leading-relaxed text-gray-600 sm:mb-4 sm:text-sm">
              {translate('resend_reset_link_modal_hint')}
            </p>
            <form className={formStackClass} onSubmit={onSubmitForm} noValidate>
              <LabeledInputField
                id="create-pw-resend-email"
                label={translate('email')}
                type="email"
                autoComplete="email"
                value={email}
                error={emailError}
                onValueChange={onEmailChange}
              />
              <button type="submit" className={authSubmitButtonClass}>
                {translate('get_reset_email')}
              </button>
            </form>
            {apiError && (
              <Alert className="mt-3 text-sm" variant="danger">
                {apiError}
              </Alert>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 sm:py-8">
            <CircularProgressBar size={5} />
            <p className="mt-3 mb-0 text-sm text-gray-600 sm:mt-4">{translate('please_wait')}…</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

const CreatePassword = () => {
  const { key } = useParams();
  const history = useHistory();
  const { translate } = useContext(LocalizeContext);
  const { isUserLoggedIn } = useContext(UserContext);

  const [findingUser, setFindingUser] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [user, setUser] = useState(null);

  const [values, setValues] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyForm);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendValues, setResendValues] = useState(emptyResendForm);
  const [resendErrors, setResendErrors] = useState(emptyResendForm);
  const [resendApiError, setResendApiError] = useState('');
  const [resendSubmitting, setResendSubmitting] = useState(false);

  if (isUserLoggedIn) {
    return <Redirect to="/" />;
  }

  const resetResendModalFields = () => {
    setResendValues(emptyResendForm);
    setResendErrors(emptyResendForm);
    setResendApiError('');
  };

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleResendChange = (key, val) => {
    setResendValues(prev => ({ ...prev, [key]: val }));
    if (resendErrors[key]) setResendErrors(prev => ({ ...prev, [key]: '' }));
  };

  const openResendModal = () => {
    resetResendModalFields();
    setResendModalOpen(true);
  };

  const closeResendModal = () => {
    if (resendSubmitting) return;
    setResendModalOpen(false);
  };

  const handleResendSubmit = async e => {
    e.preventDefault();
    setResendApiError('');
    const email = resendValues.email.trim();
    if (!email || !validateEmail(email)) {
      setResendErrors({ email: translate('invalid_email_short') });
      return;
    }
    setResendErrors(emptyResendForm);
    setResendSubmitting(true);
    try {
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.FORGOT_PASSWORD,
        requestBody: { email },
        method: 'POST'
      });
      if (error) {
        setResendApiError(error);
        return;
      }
      if (response) {
        toast.success('Password reset link has been sent');
      }
      setResendModalOpen(false);
    } catch (err) {
      setResendApiError(translate('resend_reset_link_error'));
      console.log(err);
    } finally {
      setResendSubmitting(false);
    }
  };

  const findUser = async () => {
    try {
      setFindingUser(true);
      setLoadError(null);
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.USERS_DETAILS(key),
        method: 'GET'
      });

      if (error) {
        setUser(null);
        setLoadError(error);
        return;
      }

      setUser(response ?? null);
    } catch (e) {
      console.log(e);
      setUser(null);
      setLoadError('Something went wrong');
    } finally {
      setFindingUser(false);
    }
  };

  useEffect(() => {
    if (key) findUser();
  }, [key]);

  const handlePasswordSubmit = async e => {
    e.preventDefault();
    setApiError('');
    const newErrors = {
      password: !values.password.trim() ? translate('field_required') : '',
      confirmPassword: !values.confirmPassword.trim() ? translate('field_required') : ''
    };
    if (values.password && values.confirmPassword && values.password !== values.confirmPassword) {
      newErrors.confirmPassword = PASSWORD_MISMATCH;
    }

    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.RESET_PASSWORD,
        requestBody: {
          email: user?.email,
          temporaryKey: key,
          newPassword: values.password
        },
        method: 'POST'
      });
      if (response) {
        toast.success('Password updated successfully. You can login using your new password');
        history.push('/login');
      }
      if (error) {
        setApiError(error);
      }
    } catch (submitErr) {
      console.log(submitErr);
      setApiError('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const mainContent = findingUser ? (
    <CreatePasswordLoadingState />
  ) : loadError ? (
    <InvalidResetTokenSection onResendClick={openResendModal} />
  ) : (
    <CreatePasswordForm
      user={user}
      values={values}
      errors={errors}
      submitting={submitting}
      apiError={apiError}
      onSubmit={handlePasswordSubmit}
      onFieldChange={handleChange}
    />
  );

  return (
    <div className="flex min-h-dvh w-full flex-col overflow-x-hidden bg-brand-surface font-sans text-brand-navy lg:min-h-screen lg:flex-row lg:overflow-hidden">
      <BrandAside titleKey="reset_password_brand_headline" taglineKey="reset_password_brand_tagline" />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:min-h-screen">
        <header className="flex w-full flex-wrap items-center justify-end gap-x-3 gap-y-3 border-b border-brand-navy/6 bg-brand-surface px-4 py-3.5 sm:gap-x-5 sm:px-[clamp(1.25rem,4vw,3rem)] sm:py-5">
          <div className="flex flex-shrink-0 items-center gap-4 sm:gap-6">
            <LanguagePicker />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col items-stretch justify-start px-4 py-6 sm:justify-center sm:px-[clamp(1.25rem,5vw,3rem)] sm:py-10 md:py-12 md:pb-16">
          <div className="w-full max-w-auth-form self-center">{mainContent}</div>
        </main>
      </div>

      <ResendPasswordModal
        open={resendModalOpen}
        onClose={closeResendModal}
        submitting={resendSubmitting}
        email={resendValues.email}
        emailError={resendErrors.email}
        onEmailChange={v => handleResendChange('email', v)}
        apiError={resendApiError}
        onSubmitForm={handleResendSubmit}
      />
    </div>
  );
};

export default CreatePassword;
